import { Injectable } from "@angular/core";
import { Observable, throwError, BehaviorSubject, of } from "rxjs";
import { BookingPayload, Reservation } from "../../types/booking.type";
import { BookingPayloadReschedule } from "../../types/booking.type";
import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
} from "@angular/common/http";
import { AppSettings } from "../../PRE-LOAD/app-settings";
import { catchError, tap, map, switchMap } from "rxjs/operators";
import { Router } from "@angular/router";
import { UserSelectionsService } from "../user-selections/user-selections.service";
import { E2eService } from "../../services/e2e/e2e.service";
import { environment } from "../../../../environments/environment";
import { Utilities } from "../../utilities/utilities";
import { buildUrl } from "../../../../urls";
import { AuthService } from "../../auth/auth.service";

@Injectable({
    providedIn: "root",
})
export class ReservationService {
    private reservation: Reservation;
    public reservationId: string;
    public $reservation: BehaviorSubject<Reservation> = new BehaviorSubject(
        null
    );
    private reservationsEndpoint = buildUrl(
        this.appSettings.realtimeEndpointUrl,
        "/api/booking"
    );

    /**
     * @description Verify the payload contains valid information.
     */
    private static verifyPayload(payload: BookingPayload): boolean {
        for (const prop in payload) {
            // prop !== 'satelliteRetailerCode' needs to be added to exclude the 'satelliteRetailerCode' property check
            // 'satelliteRetailerCode' can be null, so this will avoid the app to fail (because of 'payload[prop] === null')
            if (
                prop !== "satelliteRetailerCode" &&
                payload.hasOwnProperty(prop) &&
                payload[prop] === null
            ) {
                if (!environment.production) {
                    console.error("Payload is missing:", prop);
                }
                return false;
            }
        }
        return true;
    }

    /**
     * @description Verify the payload contains valid information.
     */
    private static verifyPayloadReschedule(
        payload: BookingPayloadReschedule
    ): boolean {
        for (const prop in payload) {
            if (payload.hasOwnProperty(prop) && payload[prop] === null) {
                if (!environment.production) {
                    console.error("Payload is missing:", prop);
                }
                return false;
            }
        }
        return true;
    }

    constructor(
        private http: HttpClient,
        private appSettings: AppSettings,
        private router: Router,
        private userSelectionService: UserSelectionsService,
        private e2eService: E2eService,
        private utilities: Utilities,
        private authService: AuthService
    ) {}

    /**
     * @description Submits booking information to the backend.
     */
    postReservation(): Observable<any> {
        const payload = this.buildPayload();

        if (!ReservationService.verifyPayload(payload)) {
            this.router.navigate(["/error"], { skipLocationChange: true });
            return of(false);
        }
        const req = this.http.post(this.reservationsEndpoint, payload, {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
        });
        return req.pipe(
            catchError(this.handleSubmitBookingError),
            tap((res) => {
                this.reservation = res;
                this.reservationId = res.id;
                this.$reservation.next(res);
            })
        );
    }

    /**
     * @description Get a reservation.
     */
    public getReservation(id: string): Observable<any> {
        let activeProducts;

        return this.authService.getToken().pipe(
            switchMap((token) => {
                return this.getReservationWithToken(id, token);
            })
        );
    }

    getReservationWithToken(id: string, token: string): Observable<any> {
        return this.http
            .get(`${this.reservationsEndpoint}/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .pipe(
                catchError(this.handleSubmitBookingError),
                map((res: any) => {
                    return {
                        id: res.id,
                        start: new Date(res.start),
                        end: new Date(res.end),
                        modelTreeRef: res.modelTreeReference,
                        status: res.status,
                    } as Reservation;
                }),
                tap((reservation) => {
                    this.reservation = reservation;
                    this.$reservation.next(reservation);
                })
            );
    }

    /**
     * @description Patch a reservation.
     */
    patchReservation(reservation: Reservation): Observable<any> {
        return this.http
            .patch(
                `${this.reservationsEndpoint}/${reservation.id}`,
                reservation,
                {
                    headers: {
                        Authorization: `Bearer ${sessionStorage.getItem(
                            "token"
                        )}`,
                    },
                }
            )
            .pipe(
                catchError(this.handleSubmitBookingError),
                tap((res) => {
                    this.reservation = res;
                    this.$reservation.next(res);
                })
            );
    }

    /**
     * @description Cancel the reservation.
     */
    cancelReservation(
        reservation: Reservation = this.reservation
    ): Observable<any> {
        reservation.status = "cancelled";
        return this.patchReservation(this.reservation).pipe(
            tap((res) => {
                this.reservation = res;
                this.$reservation.next(res);
            })
        );
    }

    /**
     * @description Submits booking information to the backend.
     */

    rescheduleReservation(): Observable<any> {
        const payload = this.buildPayloadReschedule();

        if (!ReservationService.verifyPayloadReschedule(payload)) {
            this.router.navigate(["/error"], { skipLocationChange: true });
            return of(false);
        }
        const req = this.http.post(
            this.reservationsEndpoint + "/reschedule",
            payload,
            {
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem("token")}`,
                },
            }
        );
        return req.pipe(
            catchError(this.handleSubmitBookingError),
            tap((res) => {
                this.reservation = res;
                this.reservationId = res.id;
                this.$reservation.next(res);
            })
        );
    }

    /**
     * Handle HTTP response errors for submit booking requests.
     * @description Handles errors returned for submit booking requests. Different error codes
     * will cause UI changes. E.g, time slot not available will show a error page.
     */
    private handleSubmitBookingError(
        error: HttpErrorResponse
    ): Observable<string> {
        return throwError(error);
    }

    /**
     * @description Build a BookingPayload from state data for use in API requests
     * to create reservations.
     */
    private buildPayload(): BookingPayload {
        var marketingPreferences = this.userSelectionService.get(
            "marketingPreferences"
        )
            ? this.userSelectionService.get("marketingPreferences")
            : {};
        marketingPreferences.version = this.appSettings.policyVersion;

        let hasBookableId =
            this.userSelectionService.get("bookableId") != undefined &&
            this.userSelectionService.get("product").modelTreeRef == undefined;

        let startTime = this.toISOStringWithTimezone(
            this.userSelectionService.get("startTime")
        );
        let endTime = this.toISOStringWithTimezone(
            this.userSelectionService.get("endTime")
        );

        const modelTreeRef = this.getModelTreeRef();
        return {
            modelTreeRef: modelTreeRef,
            retailerId: this.appSettings.retailerId,
            bookableId: hasBookableId
                ? this.userSelectionService.get("bookableId")
                : undefined,
            correlationReference: this.e2eService.correlationReference,
            overridePdiImport: this.appSettings.overridePdiImport,
            overridePdiSource: this.appSettings.overridePdiSource,
            personalInfo: {
                firstName: this.userSelectionService.get("userDetails")
                    ? this.userSelectionService.get("userDetails").firstname
                    : null,
                lastName: this.userSelectionService.get("userDetails")
                    ? this.userSelectionService.get("userDetails").lastname
                    : null,
                email: this.userSelectionService.get("userDetails")
                    ? this.userSelectionService.get("userDetails").emailAddress
                    : null,
                phone: this.userSelectionService.get("userDetails")
                    ? this.userSelectionService.get("userDetails").phoneNumber
                    : null,
                marketingPreferences: marketingPreferences,
            },
            bookingInfo: {
                comments: this.userSelectionService.get("modelPreferences"),
                start: startTime,
                end: endTime,
                startLocation: this.userSelectionService.get("startLocation")
                    ? this.userSelectionService.get("startLocation")
                    : null,
                offSiteLocation: this.utilities.offSitePayload(),
                seats: this.userSelectionService.get("userDetails")
                    ? this.userSelectionService.get("userDetails")
                          .seatsPerBooking
                    : 0,
            },
        } as BookingPayload;
    }

    private toISOStringWithTimezone = (date) => {
        const tzOffset = -date.getTimezoneOffset();
        const diff = tzOffset >= 0 ? "+" : "-";
        const pad = (n) => `${Math.floor(Math.abs(n))}`.padStart(2, "0");
        return (
            date.getFullYear() +
            "-" +
            pad(date.getMonth() + 1) +
            "-" +
            pad(date.getDate()) +
            "T" +
            pad(date.getHours()) +
            ":" +
            pad(date.getMinutes()) +
            ":" +
            pad(date.getSeconds()) +
            diff +
            pad(tzOffset / 60) +
            ":" +
            pad(tzOffset % 60)
        );
    };

    private getModelTreeRef() {
        if ((this.appSettings.eventModelTreeRef != undefined &&
            this.appSettings.eventModelTreeRef.length > 0) &&
            this.appSettings.eventJourney
        ) {
            return this.appSettings.eventModelTreeRef;
        }

        let userDetails = this.userSelectionService.get("userDetails");

        if (this.appSettings.apiBrand.includes("bmw")) {
            return this.userSelectionService.get("product")
            ? this.userSelectionService.get("product").modelTreeRef : null;
        } 
        else 
        {
            return userDetails
            ? userDetails.productVariant
            : null;
        }
     
    }

   

    private buildPayloadReschedule(): BookingPayloadReschedule {
        return {
            id: this.appSettings.rescheduleId,
            start: this.userSelectionService.get("startTime").toISOString(),
            end: this.userSelectionService.get("endTime").toISOString(),
        } as BookingPayloadReschedule;
    }
}
